/**
 * Invoice List Page (standalone)
 *
 * Lists ALL invoices across the company (not scoped to a single project).
 * Provides search, status/date filters, summary stats and CRUD entry points.
 *
 * @module pages/invoices/InvoiceListPage
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Collapse,
  Dropdown,
  Modal,
} from 'react-bootstrap';

import {
  listInvoices,
  createInvoice,
  deleteInvoice,
  generateFromQuotation,
} from '../../services/invoiceService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import KebabMenu from '../../components/common/KebabMenu';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { formatCurrency, formatDate } from '../../utils/formatters';
import {
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_VARIANTS,
} from '../../utils/constants';
import { toast } from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import StatCard from '../../components/common/StatCard';
import InvoiceForm from '../../components/invoices/InvoiceForm';
import InvoiceDetail from '../../components/invoices/InvoiceDetail';
import { exportInvoiceToPDF } from '../../utils/invoiceExport';

const PAGE_SIZE = 20;

// Summary is computed client-side from the already-fetched invoice list
// (avoids dependency on the invoice_summary view which may have RLS issues).
// Overdue is derived per-invoice via is_overdue (see utils/invoiceHelpers,
// issue #278) rather than trusted from the raw `status` column, since
// nothing ever transitions it to 'overdue' automatically. Open/overdue are
// kept mutually exclusive, matching the invoice_summary DB view.
const computeSummary = (invoices) => {
  return {
    total_billed: invoices.reduce((s, inv) => s + (inv.amount || 0), 0),
    total_paid: invoices
      .filter((inv) => inv.status === 'paid')
      .reduce((s, inv) => s + (inv.amount || 0), 0),
    total_open: invoices
      .filter(
        (inv) =>
          ['sent', 'draft', 'pending', 'partial_payment'].includes(inv.status) &&
          !inv.is_overdue
      )
      .reduce((s, inv) => s + (inv.amount || 0), 0),
    total_overdue: invoices
      .filter((inv) => inv.is_overdue)
      .reduce((s, inv) => s + (inv.amount || 0), 0),
  };
};

const InvoiceListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  // En `invoices` la escritura la conceden SÓLO `admin_all_invoices` y
  // `admin_full_access_invoices` (ambas `ALL` con `is_admin()`); no hay
  // ninguna policy de INSERT/UPDATE/DELETE para técnico, supervisor ni
  // cliente. Y el techo RESTRICTIVE `invoices_ceiling_select`
  // (`is_admin() OR NOT is_technician()`) hace que el técnico ni siquiera vea
  // filas: la pantalla le quedaba vacía y con un botón "Nueva Factura" que
  // sólo podía devolverle un error de RLS. Verificado por SELECT sobre
  // pg_policies en producción el 28-jul-2026.
  const canEdit = isAdmin;
  const { viewMode, setViewMode, isTableView } = useViewToggle();

  // Deep links (e.g. the admin dashboard's "Vencido" stat card) land on
  // /facturas?status=overdue — honor the initial status filter from the URL.
  // Only the initial value is read; the filter UI takes over afterwards.
  const [searchParams] = useSearchParams();
  const initialStatus = useMemo(() => {
    const status = searchParams.get('status');
    return status && Object.prototype.hasOwnProperty.call(INVOICE_STATUS_LABELS, status)
      ? status
      : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filters, setFilters] = useState({
    status: initialStatus,
    search: '',
    dateFrom: '',
    dateTo: '',
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [searchInput, setSearchInput] = useState('');
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(!!initialStatus);
  const searchTimeoutRef = useRef(null);

  // Modals / state
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch invoices
  //
  // `unwrapServiceResult` no es decoración: `listInvoices` cierra con
  // `catch { return { data:null, count:0, error } }`, o sea RESUELVE. Sin el
  // unwrap el `error` de react-query es estructuralmente siempre null, el
  // bloque `{error ? <Alert>…}` de más abajo queda inalcanzable y una caída de
  // red se pinta como "No se encontraron facturas" con $0.00 en las cuatro
  // tarjetas. Ver `src/utils/serviceResult.js`.
  const {
    data: invoicesResp,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['invoices', filters],
    queryFn: () =>
      unwrapServiceResult(
        listInvoices({
          status: filters.status || undefined,
        })
      ),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev) => prev,
  });

  const allInvoices = useMemo(() => invoicesResp?.data || [], [invoicesResp]);

  // Compute summary stats client-side from the already-fetched list (Issue #66)
  const summary = useMemo(() => computeSummary(allInvoices), [allInvoices]);

  // Client-side filter for search/date (service only supports status/projectId/clientId)
  const filteredInvoices = useMemo(() => {
    let list = allInvoices;
    const search = (filters.search || '').trim().toLowerCase();
    if (search) {
      list = list.filter((inv) => {
        const number = (inv.invoice_number || '').toLowerCase();
        const projNum = (inv.project?.project_number || '').toLowerCase();
        const projTitle = (inv.project?.title || '').toLowerCase();
        const clientName = (inv.client?.company_name || '').toLowerCase();
        return (
          number.includes(search) ||
          projNum.includes(search) ||
          projTitle.includes(search) ||
          clientName.includes(search)
        );
      });
    }
    if (filters.dateFrom) {
      list = list.filter((inv) => (inv.invoice_date || '') >= filters.dateFrom);
    }
    if (filters.dateTo) {
      list = list.filter((inv) => (inv.invoice_date || '') <= filters.dateTo);
    }
    return list;
  }, [allInvoices, filters.search, filters.dateFrom, filters.dateTo]);

  // Pagination (client-side over filtered list)
  const totalCount = filteredInvoices.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / filters.pageSize));
  const currentPage = Math.min(filters.page + 1, totalPages);
  const pageStart = filters.page * filters.pageSize;
  const pageInvoices = filteredInvoices.slice(pageStart, pageStart + filters.pageSize);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 0,
    }));
  };

  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: value, page: 0 }));
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const activeFiltersCount = useMemo(() => {
    let n = 0;
    if (filters.status) n += 1;
    if (filters.dateFrom) n += 1;
    if (filters.dateTo) n += 1;
    return n;
  }, [filters.status, filters.dateFrom, filters.dateTo]);

  const hasActiveFilters = activeFiltersCount > 0 || !!filters.search;

  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      status: '',
      search: '',
      dateFrom: '',
      dateTo: '',
      page: 0,
    }));
    setSearchInput('');
  };

  const handlePreviousPage = () => {
    if (filters.page > 0) handleFilterChange('page', filters.page - 1);
  };
  const handleNextPage = () => {
    if (filters.page < totalPages - 1) handleFilterChange('page', filters.page + 1);
  };

  const handleCreateInvoice = async (data) => {
    try {
      setSaving(true);
      let result;
      if (data.type === 'from_quotation') {
        result = await generateFromQuotation(data.project_id, data.quotation_id, {
          invoice_date: data.invoice_date,
          payment_terms: data.payment_terms,
          notes: data.notes,
        });
      } else {
        result = await createInvoice(data.invoice_data, data.items);
      }
      if (result.error) throw result.error;
      toast.success('Factura creada exitosamente');
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    } catch (err) {
      console.error('Create invoice error:', err);
      // #629: el toast genérico se comía el motivo. Las compuertas de
      // facturación (margen sin definir, cotización en borrador) responden
      // QUÉ FALTA; si el mensaje no llega a la pantalla, el usuario ve
      // "Error al crear la factura" y no tiene forma de destrabarse.
      toast.error(err?.message || 'Error al crear la factura');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    try {
      const result = await deleteInvoice(invoiceToDelete.id);
      if (result.error) throw result.error;
      toast.success('Factura eliminada');
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    } catch (err) {
      toast.error('Error al eliminar factura');
    }
  };

  const handleGeneratePDF = async (invoice) => {
    try {
      // Fetch full invoice (with items + payments) before generating
      const { getInvoice } = await import('../../services/invoiceService');
      const { data: full, error: pdfError } = await getInvoice(invoice.id);
      if (pdfError) throw pdfError;
      await exportInvoiceToPDF(full);
      toast.success('PDF generado');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Error al generar PDF');
    }
  };

  // Show invoice detail full-page
  if (selectedInvoiceId) {
    return (
      <DashboardLayout>
      <Container fluid>
        <InvoiceDetail
          invoiceId={selectedInvoiceId}
          onBack={() => setSelectedInvoiceId(null)}
        />
      </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <Container fluid>
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-1">
            <i className="bi bi-receipt-cutoff me-2"></i>
            Facturas
          </h2>
          <p className="text-muted mb-0">
            {isError ? (
              'No se pudo leer el listado'
            ) : (
              <>
                {totalCount} {totalCount === 1 ? 'factura' : 'facturas'}
                {hasActiveFilters ? ' (filtradas)' : ''}
              </>
            )}
          </p>
        </Col>
        <Col xs="auto">
          {canEdit && (
            <Button variant="primary" onClick={() => setShowForm(true)}>
              <i className="bi bi-plus-circle me-2"></i>
              Nueva Factura
            </Button>
          )}
        </Col>
      </Row>

      {/* Stats
          Con la lectura caída estas cuatro tarjetas no pueden decir $0.00:
          "la empresa no facturó nada" es un dato de negocio, no un error de
          red. Un guion no afirma nada, y el Alert de abajo explica por qué.

          #617 — estos cuatro totales van SIN moneda a propósito, y no es un
          descuido: suman importes de facturas distintas, y `invoices.currency`
          es por factura. Mientras la suma pueda mezclar monedas no existe UNA
          moneda que rotularla sin mentir; el rótulo correcto sale recién
          cuando el total se calcule por moneda. Las filas de la tabla, que sí
          tienen su dato, sí van rotuladas. */}
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Total Facturado"
            value={isError ? '—' : formatCurrency(summary?.total_billed || 0)}
            icon="bi-cash-stack"
            data-testid="invoice-total-billed"
            color="primary"
            loading={isLoading}
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Pagado"
            value={isError ? '—' : formatCurrency(summary?.total_paid || 0)}
            icon="bi-check-circle"
            data-testid="invoice-total-paid"
            color="success"
            loading={isLoading}
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Por Cobrar"
            value={isError ? '—' : formatCurrency(summary?.total_open || 0)}
            icon="bi-hourglass-split"
            data-testid="invoice-total-open"
            color={!isError && Number(summary?.total_open) > 0 ? 'warning' : 'secondary'}
            loading={isLoading}
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Vencido"
            value={isError ? '—' : formatCurrency(summary?.total_overdue || 0)}
            icon="bi-exclamation-triangle"
            data-testid="invoice-total-overdue"
            color={!isError && Number(summary?.total_overdue) > 0 ? 'danger' : 'secondary'}
            loading={isLoading}
          />
        </Col>
      </Row>

      {/* Filters */}
      <div className="mb-4">
        <div className="d-flex gap-2 align-items-stretch flex-wrap">
          <InputGroup className="flex-grow-1" style={{ minWidth: '250px' }}>
            <InputGroup.Text>
              {isFetching && !isLoading ? (
                <Spinner animation="border" size="sm" className="text-muted" />
              ) : (
                <i className="bi bi-search"></i>
              )}
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar por # factura, proyecto o cliente..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label="Buscar facturas"
            />
          </InputGroup>
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          <Button
            variant={activeFiltersCount > 0 ? 'primary' : 'outline-secondary'}
            onClick={() => setIsFiltersExpanded((v) => !v)}
            aria-expanded={isFiltersExpanded}
          >
            <i className={`bi bi-chevron-${isFiltersExpanded ? 'up' : 'down'}`}></i>
            <span className="d-none d-md-inline ms-1">Filtros</span>
            {activeFiltersCount > 0 && (
              <Badge bg="light" text="dark" pill className="ms-2">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
        </div>

        <Collapse in={isFiltersExpanded}>
          <div>
            <Card className="mt-3">
              <Card.Body>
                <Row className="g-2 g-md-3">
                  <Col xs={12} sm={6} lg={4}>
                    <Form.Label className="small text-muted mb-1">Estado</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="">Todos los estados</option>
                      {Object.entries(INVOICE_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={6} sm={6} lg={4}>
                    <Form.Label className="small text-muted mb-1">Desde</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={filters.dateFrom}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    />
                  </Col>
                  <Col xs={6} sm={6} lg={4}>
                    <Form.Label className="small text-muted mb-1">Hasta</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    />
                  </Col>
                </Row>
                {hasActiveFilters && (
                  <div className="d-flex justify-content-end mt-3">
                    <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                      <i className="bi bi-x-circle me-1"></i>
                      Limpiar filtros
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </Collapse>
      </div>

      {/* Content */}
      {error ? (
        <Alert variant="danger">
          <Alert.Heading>Error al cargar facturas</Alert.Heading>
          <p className="mb-2">{error.message}</p>
          <Button size="sm" variant="outline-danger" onClick={() => refetch()}>
            Reintentar
          </Button>
        </Alert>
      ) : isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="text-muted mt-2 mb-0">Cargando facturas...</p>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="bi bi-receipt fs-1 text-muted"></i>
            <h5 className="mt-3 text-muted">No se encontraron facturas</h5>
            <p className="text-muted mb-0">
              {hasActiveFilters
                ? 'Intenta cambiar los filtros'
                : canEdit
                ? 'Crea tu primera factura'
                : 'No hay facturas disponibles'}
            </p>
          </Card.Body>
        </Card>
      ) : isTableView ? (
        <Card>
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th># Factura</th>
                  <th>Proyecto</th>
                  <th>Cliente</th>
                  <th className="text-end">Monto</th>
                  <th className="text-end">Pagado</th>
                  <th className="text-end">Saldo</th>
                  <th>Estado</th>
                  <th>Vencimiento</th>
                  <th style={{ width: '40px' }}></th>
                </tr>
              </thead>
              <tbody>
                {pageInvoices.map((inv) => {
                  const effectiveStatus = inv.effective_status || inv.status;
                  const variant = INVOICE_STATUS_VARIANTS[effectiveStatus] || 'secondary';
                  const label = INVOICE_STATUS_LABELS[effectiveStatus] || effectiveStatus;
                  return (
                    <tr key={inv.id} className="align-middle">
                      <td>
                        <Button
                          variant="link"
                          className="p-0 fw-bold"
                          onClick={() => setSelectedInvoiceId(inv.id)}
                        >
                          {inv.invoice_number}
                        </Button>
                      </td>
                      <td>
                        {inv.project ? (
                          <span>
                            <strong>{inv.project.project_number}</strong>
                            <br />
                            <small className="text-muted">{inv.project.title}</small>
                          </span>
                        ) : '-'}
                      </td>
                      <td>{inv.client?.company_name || '-'}</td>
                      <td className="text-end">{formatCurrency(inv.amount, inv.currency)}</td>
                      <td className="text-end text-success">
                        {formatCurrency(inv.paid_amount || 0, inv.currency)}
                      </td>
                      <td className="text-end text-danger">
                        {formatCurrency(inv.balance || 0, inv.currency)}
                      </td>
                      <td><Badge bg={variant}>{label}</Badge></td>
                      <td>{formatDate(inv.due_date)}</td>
                      <td className="text-center">
                        <KebabMenu
                          label={`Acciones de la factura ${inv.invoice_number}`}
                          stopPropagation={false}
                        >
                          <Dropdown.Item onClick={() => setSelectedInvoiceId(inv.id)}>
                            <i className="bi bi-eye me-2"></i>Ver
                          </Dropdown.Item>
                          {canEdit && inv.project_id && (
                            <Dropdown.Item
                              onClick={() => navigate(`/proyectos/${inv.project_id}`)}
                            >
                              <i className="bi bi-pencil me-2"></i>Editar
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item onClick={() => handleGeneratePDF(inv)}>
                            <i className="bi bi-file-earmark-pdf me-2"></i>Generar PDF
                          </Dropdown.Item>
                          {canEdit && inv.status === 'draft' && (
                            <>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                className="text-danger"
                                onClick={() => {
                                  setInvoiceToDelete(inv);
                                  setShowDeleteModal(true);
                                }}
                              >
                                <i className="bi bi-trash me-2"></i>Eliminar
                              </Dropdown.Item>
                            </>
                          )}
                        </KebabMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-3">
          {pageInvoices.map((inv) => {
            const effectiveStatus = inv.effective_status || inv.status;
            const variant = INVOICE_STATUS_VARIANTS[effectiveStatus] || 'secondary';
            const label = INVOICE_STATUS_LABELS[effectiveStatus] || effectiveStatus;
            return (
              <Col xs={12} sm={6} lg={4} key={inv.id}>
                <Card className="shadow-sm h-100">
                  <Card.Body style={{ position: 'relative' }}>
                    <KebabMenu
                      label={`Acciones de la factura ${inv.invoice_number}`}
                      stopPropagation={false}
                      dropdownProps={{ style: { position: 'absolute', top: '12px', right: '12px' } }}
                    >
                      <Dropdown.Item onClick={() => setSelectedInvoiceId(inv.id)}>
                        <i className="bi bi-eye me-2"></i>Ver
                      </Dropdown.Item>
                      {canEdit && inv.project_id && (
                        <Dropdown.Item
                          onClick={() => navigate(`/proyectos/${inv.project_id}`)}
                        >
                          <i className="bi bi-pencil me-2"></i>Editar
                        </Dropdown.Item>
                      )}
                      <Dropdown.Item onClick={() => handleGeneratePDF(inv)}>
                        <i className="bi bi-file-earmark-pdf me-2"></i>Generar PDF
                      </Dropdown.Item>
                      {canEdit && inv.status === 'draft' && (
                        <>
                          <Dropdown.Divider />
                          <Dropdown.Item
                            className="text-danger"
                            onClick={() => {
                              setInvoiceToDelete(inv);
                              setShowDeleteModal(true);
                            }}
                          >
                            <i className="bi bi-trash me-2"></i>Eliminar
                          </Dropdown.Item>
                        </>
                      )}
                    </KebabMenu>

                    <div className="mb-2 pe-4">
                      <Button
                        variant="link"
                        className="p-0 fw-bold"
                        onClick={() => setSelectedInvoiceId(inv.id)}
                      >
                        {inv.invoice_number}
                      </Button>
                    </div>

                    <Badge bg={variant} className="mb-2">{label}</Badge>

                    {inv.project && (
                      <div className="small text-muted mb-1">
                        <i className="bi bi-folder me-1"></i>
                        {inv.project.project_number} - {inv.project.title}
                      </div>
                    )}

                    {inv.client?.company_name && (
                      <div className="small text-muted mb-2">
                        <i className="bi bi-building me-1"></i>
                        {inv.client.company_name}
                      </div>
                    )}

                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-primary fw-bold fs-5">
                        {formatCurrency(inv.amount, inv.currency)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-success">
                        Pagado: {formatCurrency(inv.paid_amount || 0, inv.currency)}
                      </span>
                      <span className="text-danger">
                        Saldo: {formatCurrency(inv.balance || 0, inv.currency)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between small mt-2 pt-2 border-top">
                      <span className="text-muted">
                        <i className="bi bi-calendar3 me-1"></i>
                        {formatDate(inv.invoice_date)}
                      </span>
                      <span className="text-muted">
                        Vence: {formatDate(inv.due_date)}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* Pagination */}
      {totalCount > filters.pageSize && (
        <div className="d-flex justify-content-between align-items-center p-3 mt-3 bg-white rounded border">
          <div className="text-muted small">
            Página {currentPage} de {totalPages}
          </div>
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handlePreviousPage}
              disabled={filters.page === 0}
              className="me-2"
            >
              <i className="bi bi-chevron-left"></i> Anterior
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleNextPage}
              disabled={filters.page >= totalPages - 1}
            >
              Siguiente <i className="bi bi-chevron-right"></i>
            </Button>
          </div>
        </div>
      )}

      {/* Create modal (manual create requires projectId; we don't pass one here) */}
      <InvoiceForm
        show={showForm}
        onHide={() => setShowForm(false)}
        onSave={handleCreateInvoice}
        projectId={null}
        quotations={[]}
        loading={saving}
      />

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminacion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Estas seguro de eliminar la factura{' '}
            <strong>{invoiceToDelete?.invoice_number}</strong>?
          </p>
          <p className="text-danger mb-0">
            <i className="bi bi-exclamation-triangle me-1"></i>
            Esta accion no se puede deshacer.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <i className="bi bi-trash me-1"></i>Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </DashboardLayout>
  );
};

export default InvoiceListPage;
